import type { FC, MouseEvent } from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddRounded from '@mui/icons-material/AddRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import ShareRounded from '@mui/icons-material/ShareRounded';
import StarRounded from '@mui/icons-material/StarRounded';
import {
  Button,
  CircularProgress,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
} from '@mui/material';

import {
  FILE_TYPE_MAP,
  REPORT_TYPE_MAP,
  ReportFileType,
  ReportKind,
  ReportOrderStatus,
} from 'src/constants/report';
import type { Report } from 'src/types/report';
import { getLanguageAbbreviation, getLanguageName } from 'src/helper/helper';
import trackEvent from 'src/services/analytics/trackEvent';
import { apiPaths } from 'src/services/api/apiPaths';
import { UserContext } from 'src/components/app/context/UserContext';
import { reportEqualPredicate } from 'src/components/report/helper/helper';
import type { ReportStatusProps } from 'src/components/report/OrgUnitReportsPage/ReportStatusContext';
import { ReportStatusContext } from 'src/components/report/OrgUnitReportsPage/ReportStatusContext';
import {
  orderReport,
  reportStatusRefresh,
} from 'src/components/report/OrgUnitReportsPage/ReportStatusContext/helper';
import CustomReportsTableCells from 'src/components/report/ReportTable/components/ReportTableRow/components/CustomReportsTableCells';
import OrderButton from 'src/components/report/ReportTable/components/ReportTableRow/components/OrderButton';
import { ShoppingCartContext } from 'src/components/report/ShoppingCartContext';
import { getConfigFromMap } from 'src/components/reportConfig/helper';

export const REPORT_TABLE_ROW_IS_FAVOURITE_TEST_ID =
  'report-table-row-is_favourite';

export const REPORT_TABLE_DELETE_BUTTON_TEST_ID = 'report-table-delete-button';
export const REPORT_TABLE_SHARE_BUTTON_TEST_ID = 'report-table-share-button';
export const REPORT_TABLE_ADD_BUTTON_TEST_ID = 'report-table-add-button';
export const REPORT_TABLE_ACTION_COLUMN_TEST_ID = 'report-table-action-column';

export interface ReportTableRowProps {
  onDelete: (reportId: number) => void;
  onShare: (reportId: number) => void;
  report: Report;
  reportStatus?: ReportStatusProps;
  showCustomReports?: boolean;
  reportNamesPerReportType: { [p: string]: string };
  abbreviationPerFileType: { [p: string]: string };
  loadingReportConfigData: boolean;
}

/**
 * this component renders the rows of the report Page depending on the userType. Also it adds items to
 * the shopping cart and handles the add/checked icon.
 */
export const ReportTableRow: FC<ReportTableRowProps> = ({
  onDelete,
  onShare,
  report,
  reportStatus,
  showCustomReports,
  reportNamesPerReportType,
  abbreviationPerFileType,
  loadingReportConfigData,
}) => {
  const { t } = useTranslation(['report', 'common']);
  const { user }: any = useContext(UserContext);
  const {
    addItem,
    canUseShoppingCart,
    deleteItem,
    shoppingCartItems,
    shoppingCartLanguage,
  } = useContext(ShoppingCartContext);
  const [addable, setAddable] = useState(true);
  const isZip = report.file_type === ReportFileType.ZIP;
  const [file, setFile] = useState<string>(reportStatus?.file || report.file);
  const { setPendingReportIds } = useContext(ReportStatusContext);
  const [orderStatus, setOrderStatus] = useState<number>(
    //  alter report status in case the report arrives in the error status because
    //  a report arriving in the error state would immediatly trigger the error modal
    reportStatus?.order_status === undefined
      ? report.order_status !== ReportOrderStatus.ERROR
        ? report.order_status
        : ReportOrderStatus.DRAFT
      : reportStatus.order_status,
  );
  const checkIsAddable = () => {
    const isAddable = report
      ? !shoppingCartItems.find((shoppingCartItem) =>
          report.is_custom_report
            ? shoppingCartItem.id === report.id
            : reportEqualPredicate(shoppingCartItem, {
                file_type: report.file_type,
                organisational_unit: report.organisational_unit,
                report_type: report.report_type,
              }),
        )
      : false;

    setAddable(isAddable);
  };

  useEffect(() => {
    if (report?.order_status === ReportOrderStatus.ERROR)
      // In case the incoming report already has an error state, reset the order status
      // to prevent the error modal to show up and allow the user to retry ordering the report
      setOrderStatus(ReportOrderStatus.DRAFT);
  }, []);

  useEffect(() => {
    if (reportStatus) {
      reportStatus.order_status && setOrderStatus(reportStatus.order_status);
      reportStatus.file && setFile(reportStatus.file);
    }
  }, [reportStatus]);

  useEffect(() => {
    checkIsAddable();
  }, [shoppingCartItems]);

  const { reportName, reportFileType } = useMemo(
    () => ({
      reportName:
        reportNamesPerReportType[report.report_type] ||
        t(`report:reportType.${REPORT_TYPE_MAP[report.report_type]}`),
      reportFileType: getConfigFromMap(
        report.file_type,
        abbreviationPerFileType,
        FILE_TYPE_MAP,
      ),
    }),
    [reportNamesPerReportType, abbreviationPerFileType, report],
  );

  // Checks whether a user is allowed to share a report.
  // The user may only share his own reports or reports shared with himself.
  const canShareReport = useMemo(() => {
    if (report?.created_by) {
      if (report.created_by.id === user.id) {
        return true;
      }

      if (report?.shared_by) {
        return true;
      }
    }

    return false;
  }, []);

  const downloadButtonMessage = useMemo(() => {
    if (isZip) {
      return undefined;
    }
    if (showCustomReports) {
      return getLanguageAbbreviation(report.language)?.toUpperCase();
    }
    if (canUseShoppingCart) {
      return shoppingCartLanguage.abbreviation.toUpperCase();
    }
  }, [
    isZip,
    showCustomReports,
    canUseShoppingCart,
    report.language,
    shoppingCartLanguage.code,
  ]);

  if (!report) return null;

  const selectButtonText = showCustomReports
    ? addable
      ? t('selectReport')
      : t('removeReportSelection')
    : addable
      ? t('addToBasket')
      : t('removeFromBasket');
  const handleAddToCart = (report: Report) => {
    if (
      !!report &&
      !showCustomReports &&
      orderStatus === ReportOrderStatus.DRAFT
    ) {
      handleOrderReport();
    }
    addable ? addItem(report) : deleteItem(report);
  };

  const handleOrderReport = () =>
    orderReport(
      setOrderStatus,
      setPendingReportIds,
      report.id,
      apiPaths.ORDER_REPORT,
    );

  const handleReportStatusRefresh = () =>
    reportStatusRefresh(
      setOrderStatus,
      setFile,
      report.id,
      apiPaths.REPORT_ORDER_STATUS,
    );

  const handleDownload = (event: MouseEvent) => {
    event.stopPropagation();
    trackEvent('report_downloaded', {
      report_type: String(report.report_type),
      report_kind: report.is_custom_report
        ? ReportKind.CUSTOM
        : ReportKind.PREGEN,
      language: getLanguageAbbreviation(report.language),
      file_format: FILE_TYPE_MAP[report.file_type],
    });
  };

  return (
    <TableRow>
      <TableCell component="th" scope="row">
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          {showCustomReports && report.is_favourite && (
            <StarRounded
              data-test-id={REPORT_TABLE_ROW_IS_FAVOURITE_TEST_ID}
              fontSize="small"
              color="primary"
            />
          )}
          <span>{report.name}</span>
        </Stack>
      </TableCell>
      {canUseShoppingCart && (
        <TableCell align="center">
          {loadingReportConfigData ? (
            <CircularProgress size={20} />
          ) : (
            reportName
          )}
        </TableCell>
      )}
      {canUseShoppingCart ? (
        <TableCell align="center">
          {loadingReportConfigData ? (
            <CircularProgress size={20} />
          ) : (
            reportFileType
          )}
        </TableCell>
      ) : (
        <TableCell align="center">
          {getLanguageAbbreviation(report.language)}
        </TableCell>
      )}
      {showCustomReports && <CustomReportsTableCells report={report} t={t} />}
      <TableCell
        align="right"
        data-test-id={REPORT_TABLE_ACTION_COLUMN_TEST_ID}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            justifyContent: 'flex-end',
            pr: 1,
          }}
        >
          {canUseShoppingCart && !isZip && (
            <Tooltip title={selectButtonText}>
              <Button
                variant="squareCta"
                color="secondary"
                onClick={() => handleAddToCart(report)}
                data-test-id={REPORT_TABLE_ADD_BUTTON_TEST_ID}
              >
                {addable ? <AddRounded /> : <CheckRounded />}
              </Button>
            </Tooltip>
          )}
          {!showCustomReports && (
            <OrderButton
              handleOrderReport={handleOrderReport}
              handleReportStatusRefresh={handleReportStatusRefresh}
              modalCloseCallback={() => setOrderStatus(ReportOrderStatus.DRAFT)}
              orderStatus={orderStatus}
              language={report.language}
              t={t}
            />
          )}
          {(showCustomReports || orderStatus === ReportOrderStatus.CREATED) && (
            <Tooltip title={t('downloadSingle')}>
              <span>
                <Button
                  download
                  href={file}
                  onClick={handleDownload}
                  variant="squareCta"
                  color="secondary"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('downloadSingleLabel', {
                    language: getLanguageName(report.language),
                    name: report.name,
                  })}
                  startIcon={
                    downloadButtonMessage ? <DownloadRounded /> : undefined
                  }
                  disabled={
                    showCustomReports &&
                    ReportOrderStatus.CREATED !== orderStatus
                  }
                >
                  {downloadButtonMessage ? (
                    downloadButtonMessage
                  ) : (
                    <DownloadRounded />
                  )}
                </Button>
              </span>
            </Tooltip>
          )}

          {showCustomReports && canShareReport && (
            <>
              <Tooltip title={t('shareTip')}>
                <Button
                  variant="squareCta"
                  color="secondary"
                  onClick={() => onShare(report.id)}
                  data-test-id={REPORT_TABLE_SHARE_BUTTON_TEST_ID}
                >
                  <ShareRounded />
                </Button>
              </Tooltip>

              <Tooltip title={t('removeTip')}>
                <Button
                  variant="squareCta"
                  color="secondary"
                  onClick={() => onDelete(report.id)}
                  data-test-id={REPORT_TABLE_DELETE_BUTTON_TEST_ID}
                >
                  <DeleteRounded />
                </Button>
              </Tooltip>
            </>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
};

export default ReportTableRow;
